import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { useSyncThemeAttribute } from "../../hooks/useSyncThemeAttribute.ts";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { InputMaskEnum } from "../../enums/InputMask.ts";
import { parseJwt } from "../../utils/token.util.ts";
import { validationSchemaRegistration } from "../../schemas/validationSchema.ts";
import planosService from "../../services/planosService.ts";
import usuarioService from "../../services/usuarioService.ts";
import Input from "../Inputs/Input/index.tsx";
import PasswordInput from "../Inputs/PasswordInput/index.tsx";
import Button from "../../ui/Button.tsx";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import "../../styles/design-system.css";
import {
  LoginFormData,
  RegisterFormData,
  ForgotPasswordFormData,
} from "./types.ts";

type Screen = "login" | "register" | "forgot";

const NewLogin: React.FC = () => {
  const navigate = useNavigate();
  const { showFeedback } = useFeedback();
  const { setPlano, refreshPlano } = usePlanoContext();
  const { darkMode, toggleDarkMode } = useTheme();
  useSyncThemeAttribute();

  const [screen, setScreen] = useState<Screen>("login");
  const [isLoading, setIsLoading] = useState(false);
  // A tela de "esqueci a senha" agora so pede o e-mail. A senha nova e escolhida
  // depois, na pagina /redefinir-senha, mediante o token que chega por e-mail —
  // antes daqui ela era digitada na hora, sem nenhuma prova de que a pessoa
  // tinha acesso a caixa postal.
  const [recuperacaoEnviada, setRecuperacaoEnviada] = useState(false);

  // Erro de login exibido dentro do próprio card, que fica até a próxima
  // tentativa. O toast de 3s continua, mas sozinho ele é fácil de perder —
  // e errar a senha é justamente o momento em que a pessoa precisa ler com
  // calma o que aconteceu.
  const [erroLogin, setErroLogin] = useState<string | null>(null);

  const loginForm = useForm<LoginFormData>();
  const registerForm = useForm<RegisterFormData>({
    resolver: yupResolver(validationSchemaRegistration),
  });
  const forgotForm = useForm<ForgotPasswordFormData>();

  useEffect(() => {
    setErroLogin(null);
    setRecuperacaoEnviada(false);
  }, [screen]);

  useEffect(() => {
    const sessionExpiredMessage = localStorage.getItem("session_expired_message");
    if (sessionExpiredMessage) {
      showFeedback(sessionExpiredMessage, "error");
      localStorage.removeItem("session_expired_message");
    }
  }, [showFeedback]);

  const handleLogin = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setErroLogin(null);
      const response = await handleService({
        method: MethodsEnum.POST,
        endpoint: "/usuario/login",
        data,
      });

      const token = response?.data?.token;
      if (!token) throw new Error("Token não recebido");

      localStorage.setItem("token", token);

      const planId = parseJwt(token)?.planoModeloId;

      if (planId && planId !== "null" && planId !== "undefined") {
        const result = await planosService.getPlano(planId);
        if (result.data !== null) {
          setPlano(
            result.data.nome === "Premium Plus"
              ? "premiumPlus"
              : result.data.nome === "Premium"
              ? "premium"
              : "free"
          );
        }
      } else {
        setPlano("free");
      }

      await refreshPlano();

      showFeedback("Login realizado com sucesso", "success");
      navigate("/cirurgias");
    } catch (error: any) {
      setErroLogin(error.message || "Não foi possível entrar. Tente novamente.");
      showFeedback(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      await handleService({
        method: MethodsEnum.POST,
        endpoint: "/setup",
        data,
      });
      showFeedback("Cadastro realizado com sucesso! Faça o login.", "success");
      setScreen("login");
      registerForm.reset();
    } catch (error: any) {
      showFeedback(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = async (data: ForgotPasswordFormData) => {
    const email = (data.email || forgotForm.getValues("email") || "").trim();
    if (!email) {
      showFeedback("Informe um e-mail.", "error");
      return;
    }

    try {
      setIsLoading(true);
      const resp = await usuarioService.solicitarRecuperacaoSenha(email);
      // A resposta e a mesma exista ou nao conta com esse e-mail — de proposito,
      // pra tela nao virar um jeito de descobrir quem tem cadastro.
      setRecuperacaoEnviada(true);
      showFeedback(resp?.message || "Se houver uma conta com esse e-mail, enviamos as instruções.", "success");
    } catch (error: any) {
      showFeedback(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ds ds-login-screen">
      <button
        type="button"
        className="ds-icon-btn ds-login-theme-toggle"
        onClick={toggleDarkMode}
        aria-label="Alternar tema"
      >
        {darkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
      </button>

      <div className="ds-login-visual">
        <div className="ds-login-visual-inner">
          <div className="ds-login-brand-row">
            <div className="ds-brand-mark" style={{ width: 52, height: 52 }}>
              <img src="/logo-controlsis.png" alt="ControlSIS" />
            </div>
            <span className="ds-login-brand-name">ControlSIS</span>
          </div>
          <h1>Otimize a gestão de cirurgias e do estoque da sua OPME.</h1>
          <p>
            Centralize a gestão das cirurgias, com tudo o que sua equipe precisa em um só lugar.
          </p>
          <div className="ds-login-trust">
            <div>
              <CalendarMonthOutlinedIcon fontSize="small" />
              Agende cirurgias com facilidade e rapidez.
            </div>
            <div>
              <UpdateOutlinedIcon fontSize="small" />
              Acompanhe o status de cada procedimento em tempo real.
            </div>
            <div>
              <GroupsOutlinedIcon fontSize="small" />
              Centralize a equipe e evite falhas de comunicação.
            </div>
          </div>
        </div>
      </div>

      <div className="ds-login-form-wrap">
        <div className="ds-login-form-card">
          {screen === "login" && (
            <>
              <div className="ds-login-header">
                <div className="ds-brand-mark" style={{ width: 44, height: 44, marginBottom: 20 }}>
                  <img src="/logo-controlsis.png" alt="ControlSIS" />
                </div>
                <h2>Bem-vindo</h2>
                <p className="login-sub">Entre com as credenciais para efetuar o login.</p>
              </div>

              <form onSubmit={loginForm.handleSubmit(handleLogin)}>
                <div style={{ marginBottom: 16 }}>
                  <Input
                    label="E-mail"
                    type="email"
                    placeholder="voce@empresa.com.br"
                    register={loginForm.register("email", { required: "E-mail é obrigatório" })}
                    error={loginForm.formState.errors.email}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <PasswordInput
                    label="Senha"
                    register={loginForm.register("senha", { required: "Senha é obrigatória" })}
                    error={loginForm.formState.errors.senha}
                  />
                </div>

                <div className="ds-login-forgot">
                  <a href="#forgot" onClick={(e) => { e.preventDefault(); setScreen("forgot"); }}>
                    Esqueceu a senha?
                  </a>
                </div>

                {erroLogin && <ErrorBanner message={erroLogin} />}

                <Button type="submit" variant="primary" disabled={isLoading} style={{ width: "100%" }}>
                  {isLoading ? "Entrando..." : "Entrar"}
                </Button>

                <div className="ds-login-divider">ou</div>

                <Button
                  type="button"
                  variant="outline"
                  style={{ width: "100%" }}
                  onClick={() => setScreen("register")}
                >
                  Criar conta gratuita
                </Button>
              </form>
            </>
          )}

          {screen === "register" && (
            <>
              <div className="ds-login-header">
                <div className="ds-brand-mark" style={{ width: 44, height: 44, marginBottom: 20 }}>
                  <img src="/logo-controlsis.png" alt="ControlSIS" />
                </div>
                <h2>Criar conta</h2>
                <p className="login-sub">30 dias de teste gratuito.</p>
              </div>

              <form
                onSubmit={registerForm.handleSubmit(handleRegister)}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <Input
                  label="Nome da Empresa"
                  register={registerForm.register("nomeEmpresa")}
                  error={registerForm.formState.errors.nomeEmpresa}
                />
                <Input
                  label="CNPJ"
                  register={registerForm.register("cnpj")}
                  error={registerForm.formState.errors.cnpj}
                  format={InputMaskEnum.CNPJ}
                />
                <Input
                  label="Email da Empresa"
                  register={registerForm.register("emailEmpresa")}
                  error={registerForm.formState.errors.emailEmpresa}
                />
                <Input
                  label="Telefone"
                  register={registerForm.register("telefone")}
                  error={registerForm.formState.errors.telefone}
                  format={InputMaskEnum.BrazilPhone}
                />
                <Input
                  label="Email de Login"
                  register={registerForm.register("email")}
                  error={registerForm.formState.errors.email}
                />
                <PasswordInput
                  label="Senha"
                  register={registerForm.register("senha")}
                  error={registerForm.formState.errors.senha}
                />

                <Button type="submit" variant="primary" disabled={isLoading} style={{ width: "100%" }}>
                  {isLoading ? "Enviando..." : "Cadastrar"}
                </Button>
                <div className="ds-login-signup">
                  <a href="#login" onClick={(e) => { e.preventDefault(); setScreen("login"); }}>
                    Voltar para login
                  </a>
                </div>
              </form>
            </>
          )}

          {screen === "forgot" && (
            <>
              <h2>Redefinir senha</h2>
              <p className="login-sub">
                {recuperacaoEnviada
                  ? "Se houver uma conta com esse e-mail, o link de redefinição já está a caminho."
                  : "Informe o e-mail cadastrado. Enviaremos um link para você escolher uma nova senha."}
              </p>

              <form
                onSubmit={forgotForm.handleSubmit(handleForgot)}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <div className="ds-form-field">
                  <label htmlFor="forgot-email">E-mail</label>
                  <input
                    id="forgot-email"
                    type="email"
                    placeholder="voce@empresa.com.br"
                    {...forgotForm.register("email", { required: true })}
                  />
                  {forgotForm.formState.errors.email && (
                    <span className="ds-form-error">E-mail é obrigatório</span>
                  )}
                </div>

                {recuperacaoEnviada && (
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)", margin: 0 }}>
                    Verifique sua caixa de entrada e o spam. O link vale por 30 minutos e só pode ser
                    usado uma vez.
                  </p>
                )}

                <Button type="submit" variant="primary" disabled={isLoading} style={{ width: "100%" }}>
                  {isLoading ? "Enviando..." : recuperacaoEnviada ? "Enviar novamente" : "Enviar link de redefinição"}
                </Button>
                <div className="ds-login-signup">
                  <a href="#login" onClick={(e) => { e.preventDefault(); setScreen("login"); }}>
                    Voltar para login
                  </a>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewLogin;
